import { Inject, Injectable, SimpleChanges } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { AppConfigService } from '../../shared/appconfig/appconfig.service';

import { Organization, DeletionSummary, DeletionResult } from '../../shared/models/core.model';
import { BehaviorSubject, Observable, of, Subscription } from 'rxjs';
import { filter, shareReplay, take, tap } from 'rxjs/operators';
import { AuthService } from '../../auth/auth/auth.service';

@Injectable({
  providedIn: 'root',
})
export class OrganizationService {
  public emptyOrg: Organization = {
    name: '',
    active: true,
    userActive: false,
    tier: '',
    phone: '',
    email: '',
    country: '',
    postalCode: '',
    crmId: '',
    billingId: '',
    supportId: '',
    id: '',
    readOnly: false,
    userId: '',
  };

  private myOrgSubject = new BehaviorSubject<Organization | null>(null);
  public myOrganization$ = this.myOrgSubject.asObservable();
  private myOrgLoaded = false;
  private myOrgInflight: Observable<Organization> | null = null;
  private authStateSub: Subscription | null = null;

  constructor(
    private http: HttpClient,
    private authService: AuthService,
    @Inject('BASE_URL') baseUrl: string
  ) {
    // Reset cache on auth-state change so a same-tab logout/login cannot serve the
    // previous user's organization.
    this.authStateSub = this.authService.authState$.subscribe(() => {
      this.myOrgLoaded = false;
      this.myOrgInflight = null;
      this.myOrgSubject.next(null);
    });
  }

  empty(): Organization {
    return this.emptyOrg;
  }

  getOrganizations() {
    return this.http.get<Organization[]>(
      AppConfigService.settings.get('chatstyleapiServer') + 'organization/'
    );
  }

  getMyOrganization(): Observable<Organization> {
    if (this.myOrgLoaded && this.myOrgSubject.value) {
      return of(this.myOrgSubject.value);
    }
    if (this.myOrgInflight) {
      return this.myOrgInflight;
    }
    this.myOrgInflight = this.http
      .get<Organization>(
        AppConfigService.settings.get('chatstyleapiServer') + 'organization/me'
      )
      .pipe(
        tap({
          next: (org) => {
            this.myOrgSubject.next(org);
            this.myOrgLoaded = true;
            this.myOrgInflight = null;
          },
          error: () => {
            this.myOrgInflight = null;
          },
        }),
        shareReplay(1)
      );
    return this.myOrgInflight;
  }

  refreshMyOrganization(): Observable<Organization> {
    this.myOrgLoaded = false;
    this.myOrgInflight = null;
    return this.getMyOrganization();
  }

  updateOrganization(org: Organization) {
    return this.http
      .post<string>(
        AppConfigService.settings.get('chatstyleapiServer') +
          'organization/' +
          org.id,
        org
      )
      .pipe(
        tap(() => {
          this.myOrgLoaded = false;
          this.myOrgInflight = null;
        })
      );
  }

  getUsers(): Observable<Organization[]> {
    return this.http.get<Organization[]>(
      AppConfigService.settings.get('chatstyleapiServer') +
        'organization/users'
    );
  }

  getInvitations(): Observable<Organization[]> {
    return this.http.get<Organization[]>(
      AppConfigService.settings.get('chatstyleapiServer') +
        'organization/invitations'
    );
  }

  getMyInvitations(): Observable<Organization[]> {
    return this.http.get<Organization[]>(
      AppConfigService.settings.get('chatstyleapiServer') +
        'organization/invitations/me'
    );
  }

  setActive(id: string): Observable<any> {
    return this.http.post<string>(
      AppConfigService.settings.get('chatstyleapiServer') +
        'organization/' +
        id +
        '/active',
      {}
    );
  }

  inviteUser(email: string): Observable<any> {
    return this.http.post<string>(
      AppConfigService.settings.get('chatstyleapiServer') +
        'organization/invite/',
      { email: email }
    );
  }

  updateUserActivation(org: Organization): Observable<Organization> {
    return this.http.post<Organization>(
      AppConfigService.settings.get('chatstyleapiServer') +
        'organization/users',
      org
    );
  }

  updateInvitationActivation(org: Organization): Observable<Organization> {
    return this.http.post<Organization>(
      AppConfigService.settings.get('chatstyleapiServer') +
        'organization/invitations',
      org
    );
  }

  deleteUser(userId: string): Observable<any> {
    return this.http.delete(
      AppConfigService.settings.get('chatstyleapiServer') +
        'organization/' +
        userId
    );
  }

  deleteInvitation(email: string): Observable<any> {
    return this.http.request('DELETE',
      AppConfigService.settings.get('chatstyleapiServer') +
        'organization/invite',
      {
        body: { email }
      }
    );
  }

  // Tenant-implicit routes: the API resolves the organization from the caller's tenant context,
  // so no orgId is sent in the URL. orgId is retained in the signatures for caller compatibility.
  getDeletionSummary(_orgId?: string): Observable<DeletionSummary> {
    return this.http.get<DeletionSummary>(
      AppConfigService.settings.get('chatstyleapiServer') +
        'admin/organizations/deletion-summary'
    );
  }

  deleteOrganization(_orgId: string, confirmationName: string): Observable<DeletionResult> {
    return this.http.delete<DeletionResult>(
      AppConfigService.settings.get('chatstyleapiServer') +
        'admin/organizations',
      {
        body: { confirmationName }
      }
    );
  }
}
